<script setup>
import { computed } from 'vue';
import { useMapGetter } from 'dashboard/composables/store.js';
import Icon from 'next/icon/Icon.vue';

const props = defineProps({
  to: { type: [Object, String], default: '' },
  label: { type: String, default: '' },
  icon: { type: [String, Object], default: '' },
  expandable: { type: Boolean, default: false },
  isExpanded: { type: Boolean, default: false },
  isActive: { type: Boolean, default: false },
  hasActiveChild: { type: Boolean, default: false },
  getterKeys: { type: Object, default: () => ({}) },
  badge: { type: [Number, String], default: 0 },
});

const emit = defineEmits(['toggle']);

const showBadge = useMapGetter(props.getterKeys.badge);
const dynamicCount = useMapGetter(props.getterKeys.count);
const count = computed(() => {
  const value = props.badge || dynamicCount.value;
  return Number(value) > 99 ? '99+' : value;
});

const hasCountBadge = computed(
  () => Number(props.badge || dynamicCount.value) > 0
);
</script>

<template>
  <component
    :is="to ? 'router-link' : 'div'"
    v-tooltip.right="label"
    class="relative flex h-11 min-w-0 items-center gap-2 overflow-visible rounded-2xl px-3 py-1.5 transition-all duration-200 md:size-12 md:justify-center md:p-0"
    role="button"
    draggable="false"
    :to="to"
    :title="label"
    :class="{
      'bg-[#e7f1ff] text-n-brand shadow-sm shadow-blue-500/10 ring-1 ring-[#bfdbfe] font-semibold dark:bg-n-alpha-2 dark:ring-n-weak':
        isActive && !hasActiveChild,
      'bg-[#e7f1ff] text-n-brand shadow-sm shadow-blue-500/10 ring-1 ring-[#bfdbfe] font-semibold dark:bg-n-alpha-2 dark:ring-n-weak':
        hasActiveChild,
      'text-n-slate-10 hover:-translate-y-0.5 hover:bg-white hover:text-n-slate-12 hover:shadow-sm dark:hover:bg-n-alpha-2':
        !isActive && !hasActiveChild,
    }"
    @click.stop="emit('toggle')"
  >
    <div v-if="icon" class="relative flex items-center gap-2 overflow-visible">
      <Icon v-if="icon" :icon="icon" class="size-5" />
      <span
        v-if="showBadge"
        class="absolute -top-1 size-2.5 rounded-full bg-n-brand ltr:-right-1 rtl:-left-1"
      />
    </div>
    <div class="flex items-center gap-1.5 flex-grow min-w-0 md:hidden">
      <span class="text-sm font-medium leading-5 truncate">
        {{ label }}
      </span>
      <span
        v-if="hasCountBadge"
        class="rounded-md capitalize text-xs leading-5 font-medium text-center outline outline-1 px-1 flex-shrink-0"
        :class="{
          'text-n-blue-text outline-n-slate-6': isActive,
          'text-n-slate-11 outline-n-strong': !isActive,
        }"
      >
        {{ count }}
      </span>
    </div>
    <span
      v-if="hasCountBadge"
      class="absolute top-1 z-50 hidden h-4 min-w-4 place-content-center rounded-full bg-n-brand px-1 text-[10px] font-semibold leading-none text-white shadow-sm md:grid ltr:right-1 rtl:left-1"
    >
      {{ count }}
    </span>
    <span
      v-if="expandable"
      v-show="isExpanded"
      class="i-lucide-chevron-up size-3 md:hidden"
      @click.stop="emit('toggle')"
    />
  </component>
</template>
